export type LanguageKey = 'en' | 'zh-CN' | 'zh-TW';

export interface TranslationContent {
  layout: {
    brandName: string;
    brandTagline: string;
    nav: {
      home: string;
      dashboard: string;
      coupons: string;
      profiles: string;
      present: string;
      admin: string;
      org: string;
      subuser: string;
      login: string;
    };
    roles: {
      admin: string;
      user: string;
      subuser: string;
    };
    roleLabel: string;
    logout: string;
    navLabel: string;
    themeLabel: string;
    languageLabel: string;
    themeNames: {
      freshMint: string;
      citrusSunrise: string;
      calmSlate: string;
    };
    footer: {
      contact: string;
      email: string;
      phone: string;
      company: string;
      about: string;
      security: string;
      support: string;
      resources: string;
      guides: string;
      privacy: string;
      terms: string;
      metaTagline: string;
      copyright: string;
    };
  };
  home: {
    badge: string;
    title: string;
    description: string;
    stats: {
      schools: { label: string; value: string };
      programs: { label: string; value: string };
      assessments: { label: string; value: string };
    };
    actions: {
      title: string;
      description: string;
      links: {
        login: string;
        dashboard: string;
        coupons: string;
        profiles: string;
      };
    };
    reasons: {
      title: string;
      items: string[];
    };
  };
  login: {
    badge: string;
    title: string;
    description: string;
    username: string;
    password: string;
    error: string;
    loading: string;
    submit: string;
    demo: {
      title: string;
      description: string;
      table: {
        role: string;
        username: string;
        password: string;
      };
    };
  };
  admin: {
    badge: string;
    title: string;
    description: string;
    empty: string;
    orgSummary: {
      org: string;
      user: string;
      available: string;
      totalIssued: string;
    };
    issue: {
      title: string;
      description: string;
      amountLabel: string;
      amountPlaceholder: string;
      submit: string;
      success: string;
      error: string;
    };
    ledger: {
      title: string;
      table: {
        date: string;
        amount: string;
        actor: string;
      };
    };
  };
  orgUser: {
    badge: string;
    title: string;
    description: string;
    empty: string;
    orgSummary: {
      org: string;
      available: string;
      totalIssued: string;
      profiles: string;
      revealed: string;
    };
    allocate: {
      amountPlaceholder: string;
      submit: string;
      success: string;
      error: string;
    };
    subusers: {
      title: string;
      table: {
        name: string;
        balance: string;
        allocate: string;
      };
    };
    profiles: {
      title: string;
      hidden: string;
      table: {
        profile: string;
        subuser: string;
        status: string;
        tier: string;
      };
      status: {
        draft: string;
        applied: string;
        revealed: string;
      };
    };
  };
  subuser: {
    badge: string;
    title: string;
    description: string;
    empty: string;
    balance: {
      available: string;
      profiles: string;
      revealed: string;
    };
    create: {
      title: string;
      description: string;
      placeholder: string;
      submit: string;
      success: string;
      error: string;
    };
    apply: {
      submit: string;
      error: string;
    };
    reveal: {
      submit: string;
      error: string;
    };
    profiles: {
      title: string;
      hidden: string;
      table: {
        profile: string;
        status: string;
        tier: string;
        actions: string;
      };
      status: {
        draft: string;
        applied: string;
        revealed: string;
      };
    };
  };
  access: {
    badge: string;
    title: string;
    description: string;
    action: string;
  };
  dashboard: {
    error: string;
    loading: string;
    badge: string;
    title: string;
    description: string;
    tierLabel: string;
    metrics: {
      couponsIssued: string;
      couponValue: string;
      resultsDelivered: string;
      testsRemaining: string;
    };
    checklist: {
      title: string;
      items: string[];
    };
  };
  coupons: {
    error: string;
    loading: string;
    badge: string;
    title: string;
    description: string;
    totalValue: string;
    redeem: {
      title: string;
      description: string;
      placeholder: string;
      submit: string;
      success: string;
      failure: string;
    };
    stats: {
      redeemed: string;
      testsUnlocked: string;
      inventoryHealth: string;
      inventoryLabel: string;
    };
    pricing: {
      title: string;
      description: string;
      tiers: Array<{
        name: string;
        price: string;
        features: string;
        includes: string;
      }>;
    };
    history: {
      title: string;
      table: {
        code: string;
        value: string;
        redeemed: string;
      };
    };
  };
  profiles: {
    generatedName: string;
    loading: string;
    badge: string;
    title: string;
    description: string;
    readyLabel: string;
    history: {
      title: string;
      searchPlaceholder: string;
      table: {
        id: string;
        name: string;
        dob: string;
        created: string;
      };
    };
    upload: {
      title: string;
      description: string;
      success: string;
    };
    form: {
      title: string;
      nameLabel: string;
      namePlaceholder: string;
      dobLabel: string;
      slotLabel: string;
      slotOptions: string[];
      notesLabel: string;
      notesPlaceholder: string;
      submit: string;
    };
  };
  present: {
    loading: string;
    badge: string;
    title: string;
    description: string;
    availableLabel: string;
    select: {
      title: string;
      searchPlaceholder: string;
      actionsLabel: string;
      button: string;
      table: {
        id: string;
        name: string;
        dob: string;
        created: string;
      };
    };
    report: {
      title: string;
      assessmentId: string;
      status: string;
      loading: string;
      resultNumber: string;
      summaryTitle: string;
      summaryText: string;
      nextStepsTitle: string;
      nextStepsItems: string[];
    };
  };
}

export const languageOptions: Array<{ key: LanguageKey; label: string }> = [
  { key: 'en', label: 'English' },
  { key: 'zh-CN', label: '简体中文' },
  { key: 'zh-TW', label: '繁體中文' },
];

export const translations: Record<LanguageKey, TranslationContent> = {
  en: {
    layout: {
      brandName: 'CoopTest Portal',
      brandTagline: 'School Mental Health Operations',
      nav: {
        home: 'Home',
        dashboard: 'Dashboard',
        coupons: 'Coupons',
        profiles: 'Profiles & Tests',
        present: 'Present Results',
        admin: 'Admin Console',
        org: 'Org Console',
        subuser: 'Subuser Console',
        login: 'Login',
      },
      roles: {
        admin: 'Admin',
        user: 'Org User',
        subuser: 'Subuser',
      },
      roleLabel: 'Role: {{role}}',
      logout: 'Sign out',
      navLabel: 'Primary navigation',
      themeLabel: 'Select theme',
      languageLabel: 'Language',
      themeNames: {
        freshMint: 'Fresh Mint',
        citrusSunrise: 'Citrus Sunrise',
        calmSlate: 'Calm Slate',
      },
      footer: {
        contact: 'Contact',
        email: 'Email: hello@cooptest.com',
        phone: 'Phone: +1 (800) 555-0132',
        company: 'Company',
        about: 'About CoopTest',
        security: 'Security & Compliance',
        support: 'Support',
        resources: 'Resources',
        guides: 'Implementation Guides',
        privacy: 'Privacy Policy',
        terms: 'Terms of Use',
        metaTagline: 'Secure operations for school mental health programs',
        copyright: '© {{year}} CoopTest Health Technologies',
      },
    },
    home: {
      badge: 'Operational Hub',
      title: 'Unified portal for school-based mental health programs',
      description:
        'Manage staff onboarding, coupon redemptions, and testing workflows with a single modern workspace. Switch the theme to match your clinic environment and keep your teams focused on supporting students.',
      stats: {
        schools: { label: 'Schools served', value: '86' },
        programs: { label: 'Active programs', value: '312' },
        assessments: { label: 'Assessments delivered', value: '18K' },
      },
      actions: {
        title: 'Jump into your workflow',
        description: "Shortcuts into the portal's core operations.",
        links: {
          login: 'Staff login',
          dashboard: 'View analytics',
          coupons: 'Manage coupons',
          profiles: 'Create student profile',
        },
      },
      reasons: {
        title: 'Why CoopTest?',
        items: [
          'Streamlined digital record keeping for student wellbeing.',
          'Offline-friendly coupon redemption with audit trails.',
          'Professional reporting that schools can trust.',
        ],
      },
    },
    login: {
      badge: 'Secure access',
      title: 'Staff login',
      description:
        'Authenticated teams can access dashboards, coupon inventory, and student records.',
      username: 'Username',
      password: 'Password',
      error: 'Login failed. Check your credentials.',
      loading: 'Signing in…',
      submit: 'Login',
      demo: {
        title: 'Demo credentials',
        description: 'Use these sample accounts to explore role-based workflows.',
        table: {
          role: 'Role',
          username: 'Username',
          password: 'Password',
        },
      },
    },
    admin: {
      badge: 'Platform admin',
      title: 'Issue test credits',
      description: 'Allocate test credits to org users and monitor balances.',
      empty: 'No orgs are configured yet.',
      orgSummary: {
        org: 'Organization',
        user: 'Org user',
        available: 'Tests available',
        totalIssued: 'Total issued',
      },
      issue: {
        title: 'Issue credits',
        description: 'Send new test credits to the org user.',
        amountLabel: 'Tests to issue',
        amountPlaceholder: 'e.g. 10',
        submit: 'Issue credits',
        success: 'Credits issued to org balance.',
        error: 'Unable to issue credits. Check the amount.',
      },
      ledger: {
        title: 'Issue ledger',
        table: {
          date: 'Date',
          amount: 'Amount',
          actor: 'Actor',
        },
      },
    },
    orgUser: {
      badge: 'Org user',
      title: 'Allocate credits to subusers',
      description: 'Track org-wide balances, allocations, and profile results.',
      empty: 'No org data is available.',
      orgSummary: {
        org: 'Organization',
        available: 'Org balance',
        totalIssued: 'Total issued',
        profiles: 'Profiles tracked',
        revealed: 'Results revealed',
      },
      allocate: {
        amountPlaceholder: 'Tests',
        submit: 'Allocate',
        success: 'Allocation recorded.',
        error: 'Allocation failed. Check the balance.',
      },
      subusers: {
        title: 'Subuser allocations',
        table: {
          name: 'Subuser',
          balance: 'Available tests',
          allocate: 'Allocate tests',
        },
      },
      profiles: {
        title: 'Org profile results',
        hidden: 'Hidden',
        table: {
          profile: 'Profile',
          subuser: 'Subuser',
          status: 'Status',
          tier: 'Risk tier',
        },
        status: {
          draft: 'Draft',
          applied: 'Applied',
          revealed: 'Revealed',
        },
      },
    },
    subuser: {
      badge: 'Subuser',
      title: 'Apply tests to profiles',
      description: 'Create profiles, apply credits, and reveal results.',
      empty: 'No subuser data is available.',
      balance: {
        available: 'Available tests',
        profiles: 'Profiles created',
        revealed: 'Results revealed',
      },
      create: {
        title: 'Create a profile',
        description: 'Enter a new profile to apply test credits.',
        placeholder: 'Profile name',
        submit: 'Create profile',
        success: 'Profile created.',
        error: 'Profile could not be created.',
      },
      apply: {
        submit: 'Apply test',
        error: 'Unable to apply a test credit.',
      },
      reveal: {
        submit: 'Reveal result',
        error: 'Unable to reveal results yet.',
      },
      profiles: {
        title: 'My profiles',
        hidden: 'Hidden',
        table: {
          profile: 'Profile',
          status: 'Status',
          tier: 'Risk tier',
          actions: 'Actions',
        },
        status: {
          draft: 'Draft',
          applied: 'Applied',
          revealed: 'Revealed',
        },
      },
    },
    access: {
      badge: 'Access denied',
      title: 'You do not have access to this area.',
      description: 'Switch accounts or return to the homepage.',
      action: 'Return home',
    },
    dashboard: {
      error: 'Failed to load dashboard data.',
      loading: 'Loading dashboard…',
      badge: 'Operational KPIs',
      title: 'Dashboard overview',
      description:
        'Track coupon performance and student results to reconcile offline payments with delivered services.',
      tierLabel: 'Current tier: {{tier}}',
      metrics: {
        couponsIssued: 'Coupons issued',
        couponValue: 'Coupon value',
        resultsDelivered: 'Results delivered',
        testsRemaining: 'Tests remaining',
      },
      checklist: {
        title: 'Reconciliation checklist',
        items: [
          'Confirm offline payments match redeemed coupon count.',
          'Ensure student records are updated after each assessment.',
          'Share program highlights with school partners weekly.',
        ],
      },
    },
    coupons: {
      error: 'Failed to load coupon data.',
      loading: 'Loading coupon data…',
      badge: 'Coupons',
      title: 'Manage pricing & redemption',
      description:
        'Issue codes offline, track redemptions, and monitor remaining inventory for each school.',
      totalValue: 'Total value redeemed',
      redeem: {
        title: 'Redeem a coupon',
        description:
          'Enter a CoopTest code received from the clinic cashier to unlock services.',
        placeholder: 'Enter coupon code',
        submit: 'Redeem',
        success: 'Redeemed code {{code}}',
        failure: 'Redeem failed, please try again.',
      },
      stats: {
        redeemed: 'Coupons redeemed',
        testsUnlocked: 'Tests unlocked',
        inventoryHealth: 'Inventory health',
        inventoryLabel: '{{count}} codes',
      },
      pricing: {
        title: 'Pricing tiers',
        description:
          'Share these bundles with school partners when planning cohort purchases.',
        tiers: [
          {
            name: 'Basic',
            price: '$100',
            features: '15 tests',
            includes: 'Includes 15 assessments',
          },
          {
            name: 'Standard',
            price: '$500',
            features: '100 tests + advisory',
            includes: 'Includes 100 assessments',
          },
          {
            name: 'Premium',
            price: '$2000',
            features: '800 tests + 1:1 coaching',
            includes: 'Includes 800 assessments',
          },
        ],
      },
      history: {
        title: 'Recent redemptions',
        table: {
          code: 'Code',
          value: 'Value',
          redeemed: 'Redeemed',
        },
      },
    },
    profiles: {
      generatedName: 'Student {{index}}',
      loading: 'Loading profiles…',
      badge: 'Profiles',
      title: 'Student intake & scheduling',
      description:
        'Register students after coupon redemption, upload rosters, and track testing appointments.',
      readyLabel: 'Profiles ready',
      history: {
        title: 'Profile history',
        searchPlaceholder: 'Search by student name',
        table: {
          id: 'ID',
          name: 'Name',
          dob: 'Date of birth',
          created: 'Created',
        },
      },
      upload: {
        title: 'Batch upload roster',
        description: 'Import a CSV roster exported from the school SIS to generate accounts in bulk.',
        success: 'Queued {{file}} for processing (demo mode).',
      },
      form: {
        title: 'Create a profile & schedule test',
        nameLabel: 'Full name',
        namePlaceholder: 'e.g. Jordan Winters',
        dobLabel: 'Date of birth',
        slotLabel: 'Preferred slot',
        slotOptions: ['Morning – 09:00', 'Midday – 12:30', 'Afternoon – 15:00'],
        notesLabel: 'Notes',
        notesPlaceholder: 'Add accommodations or guardian requests.',
        submit: 'Create & assign assessment',
      },
    },
    present: {
      loading: 'Loading presentation data…',
      badge: 'Results',
      title: 'Present assessment outcomes',
      description:
        'Select a student to review their latest CoopTest results before sharing with school stakeholders.',
      availableLabel: 'Profiles available',
      select: {
        title: 'Select profile',
        searchPlaceholder: 'Search by name',
        actionsLabel: 'Actions',
        button: 'View results',
        table: {
          id: 'ID',
          name: 'Name',
          dob: 'DOB',
          created: 'Created',
        },
      },
      report: {
        title: 'Results for {{name}}',
        assessmentId: 'Assessment ID: {{id}}',
        status: 'Report ready',
        loading: 'Loading result…',
        resultNumber: 'Result number',
        summaryTitle: 'Summary',
        summaryText:
          'Student demonstrates strong resilience and benefits from structured reflection activities.',
        nextStepsTitle: 'Next steps',
        nextStepsItems: [
          'Share the report securely with the school\'s counseling lead.',
          'Schedule a follow-up session with guardians within 14 days.',
          'Log accommodations in the student record if recommended.',
        ],
      },
    },
  },
  'zh-CN': {
    layout: {
      brandName: 'CoopTest 门户',
      brandTagline: '校园心理健康运营平台',
      nav: {
        home: '首页',
        dashboard: '数据看板',
        coupons: '优惠券',
        profiles: '档案与测评',
        present: '呈现结果',
        admin: '管理员控制台',
        org: '机构控制台',
        subuser: '子用户控制台',
        login: '登录',
      },
      roles: {
        admin: '管理员',
        user: '机构用户',
        subuser: '子用户',
      },
      roleLabel: '角色：{{role}}',
      logout: '退出登录',
      navLabel: '主导航',
      themeLabel: '选择主题',
      languageLabel: '语言',
      themeNames: {
        freshMint: '清新薄荷',
        citrusSunrise: '柑橘晨光',
        calmSlate: '沉稳板岩',
      },
      footer: {
        contact: '联系方式',
        email: '邮箱：hello@cooptest.com',
        phone: '电话：+1 (800) 555-0132',
        company: '公司',
        about: '关于 CoopTest',
        security: '安全与合规',
        support: '支持',
        resources: '资源',
        guides: '实施指南',
        privacy: '隐私政策',
        terms: '使用条款',
        metaTagline: '为校园心理健康项目提供安全运营保障',
        copyright: '© {{year}} CoopTest 健康科技',
      },
    },
    home: {
      badge: '运营中心',
      title: '一体化校园心理健康项目门户',
      description:
        '通过现代化工作区统一管理员工入职、优惠券核销和测评流程。根据机构环境切换主题，让团队专注支持学生。',
      stats: {
        schools: { label: '服务学校', value: '86' },
        programs: { label: '运行项目', value: '312' },
        assessments: { label: '完成测评', value: '18K' },
      },
      actions: {
        title: '进入工作流程',
        description: '直达门户核心业务的快捷入口。',
        links: {
          login: '员工登录',
          dashboard: '查看分析',
          coupons: '管理优惠券',
          profiles: '创建学生档案',
        },
      },
      reasons: {
        title: '为什么选择 CoopTest？',
        items: [
          '为学生身心健康建立高效的电子档案。',
          '支持离线核销并保留审计记录。',
          '输出值得学校信赖的专业报告。',
        ],
      },
    },
    login: {
      badge: '安全访问',
      title: '员工登录',
      description: '授权团队可访问看板、优惠券库存和学生档案。',
      username: '用户名',
      password: '密码',
      error: '登录失败，请检查账号信息。',
      loading: '正在登录…',
      submit: '登录',
      demo: {
        title: '演示账号',
        description: '使用以下账号体验角色分工流程。',
        table: {
          role: '角色',
          username: '用户名',
          password: '密码',
        },
      },
    },
    admin: {
      badge: '平台管理员',
      title: '发放测评额度',
      description: '向机构用户发放测评额度并监控余额。',
      empty: '暂无机构配置。',
      orgSummary: {
        org: '机构',
        user: '机构用户',
        available: '可用测评',
        totalIssued: '累计发放',
      },
      issue: {
        title: '发放额度',
        description: '向机构用户追加测评额度。',
        amountLabel: '发放数量',
        amountPlaceholder: '例如 10',
        submit: '发放',
        success: '已更新机构余额。',
        error: '发放失败，请检查数量。',
      },
      ledger: {
        title: '发放记录',
        table: {
          date: '时间',
          amount: '数量',
          actor: '操作人',
        },
      },
    },
    orgUser: {
      badge: '机构用户',
      title: '分配额度给子用户',
      description: '追踪机构余额、分配情况和档案结果。',
      empty: '暂无机构数据。',
      orgSummary: {
        org: '机构',
        available: '机构余额',
        totalIssued: '累计发放',
        profiles: '档案数量',
        revealed: '已揭示结果',
      },
      allocate: {
        amountPlaceholder: '数量',
        submit: '分配',
        success: '分配已记录。',
        error: '分配失败，请检查余额。',
      },
      subusers: {
        title: '子用户分配',
        table: {
          name: '子用户',
          balance: '可用测评',
          allocate: '分配额度',
        },
      },
      profiles: {
        title: '机构档案结果',
        hidden: '未揭示',
        table: {
          profile: '档案',
          subuser: '子用户',
          status: '状态',
          tier: '风险等级',
        },
        status: {
          draft: '草稿',
          applied: '已使用',
          revealed: '已揭示',
        },
      },
    },
    subuser: {
      badge: '子用户',
      title: '为档案使用测评',
      description: '创建档案、使用额度并揭示结果。',
      empty: '暂无子用户数据。',
      balance: {
        available: '可用测评',
        profiles: '创建档案',
        revealed: '已揭示结果',
      },
      create: {
        title: '创建档案',
        description: '新增档案以使用测评额度。',
        placeholder: '档案姓名',
        submit: '创建档案',
        success: '档案已创建。',
        error: '创建失败，请重试。',
      },
      apply: {
        submit: '使用测评',
        error: '无法使用测评额度。',
      },
      reveal: {
        submit: '揭示结果',
        error: '暂无法揭示结果。',
      },
      profiles: {
        title: '我的档案',
        hidden: '未揭示',
        table: {
          profile: '档案',
          status: '状态',
          tier: '风险等级',
          actions: '操作',
        },
        status: {
          draft: '草稿',
          applied: '已使用',
          revealed: '已揭示',
        },
      },
    },
    access: {
      badge: '无访问权限',
      title: '你没有权限访问此页面。',
      description: '请切换账号或返回首页。',
      action: '返回首页',
    },
    dashboard: {
      error: '加载数据看板失败。',
      loading: '正在加载数据看板…',
      badge: '运营指标',
      title: '看板概览',
      description: '追踪优惠券表现和学生结果，对账离线付款与交付服务。',
      tierLabel: '当前套餐：{{tier}}',
      metrics: {
        couponsIssued: '发放优惠券',
        couponValue: '优惠券金额',
        resultsDelivered: '出具结果',
        testsRemaining: '剩余测评',
      },
      checklist: {
        title: '对账清单',
        items: [
          '核对离线付款与已核销优惠券数量一致。',
          '每次测评后及时更新学生档案。',
          '每周与学校伙伴分享项目亮点。',
        ],
      },
    },
    coupons: {
      error: '加载优惠券数据失败。',
      loading: '正在加载优惠券数据…',
      badge: '优惠券',
      title: '定价与核销管理',
      description: '线下发放兑换码，跟踪核销情况，并监控各校库存。',
      totalValue: '已核销总金额',
      redeem: {
        title: '核销优惠券',
        description: '输入诊所收银员提供的 CoopTest 兑换码以解锁服务。',
        placeholder: '请输入兑换码',
        submit: '核销',
        success: '成功核销兑换码 {{code}}',
        failure: '核销失败，请重试。',
      },
      stats: {
        redeemed: '已核销数量',
        testsUnlocked: '解锁测评',
        inventoryHealth: '库存健康度',
        inventoryLabel: '{{count}} 个编码',
      },
      pricing: {
        title: '定价套餐',
        description: '与学校伙伴规划批量采购时可参考以下组合。',
        tiers: [
          {
            name: '基础版',
            price: '¥100',
            features: '15 次测评',
            includes: '包含 15 份测评报告',
          },
          {
            name: '标准版',
            price: '¥500',
            features: '100 次测评 + 咨询',
            includes: '包含 100 份测评报告',
          },
          {
            name: '尊享版',
            price: '¥2000',
            features: '800 次测评 + 1:1 辅导',
            includes: '包含 800 份测评报告',
          },
        ],
      },
      history: {
        title: '最近核销记录',
        table: {
          code: '兑换码',
          value: '金额',
          redeemed: '核销日期',
        },
      },
    },
    profiles: {
      generatedName: '学生 {{index}}',
      loading: '正在加载档案…',
      badge: '档案',
      title: '学生建档与排期',
      description: '在优惠券核销后登记学生，上传名单并跟踪测评安排。',
      readyLabel: '可用档案',
      history: {
        title: '档案记录',
        searchPlaceholder: '按学生姓名搜索',
        table: {
          id: '编号',
          name: '姓名',
          dob: '出生日期',
          created: '创建时间',
        },
      },
      upload: {
        title: '批量上传名单',
        description: '导入学校教务系统导出的 CSV 名单，批量生成账号。',
        success: '已加入 {{file}} 至处理队列（演示模式）。',
      },
      form: {
        title: '创建档案并安排测评',
        nameLabel: '姓名',
        namePlaceholder: '例如：张晨曦',
        dobLabel: '出生日期',
        slotLabel: '首选时段',
        slotOptions: ['上午 09:00', '中午 12:30', '下午 15:00'],
        notesLabel: '备注',
        notesPlaceholder: '添加适配要求或家长诉求。',
        submit: '创建并分配测评',
      },
    },
    present: {
      loading: '正在加载呈现数据…',
      badge: '结果',
      title: '呈现测评结论',
      description: '选择学生查看最新 CoopTest 结果，在向学校干系人汇报前快速确认。',
      availableLabel: '可用档案',
      select: {
        title: '选择档案',
        searchPlaceholder: '按姓名搜索',
        actionsLabel: '操作',
        button: '查看结果',
        table: {
          id: '编号',
          name: '姓名',
          dob: '出生日期',
          created: '创建时间',
        },
      },
      report: {
        title: '{{name}} 的结果',
        assessmentId: '测评编号：{{id}}',
        status: '报告已就绪',
        loading: '正在载入结果…',
        resultNumber: '结果编号',
        summaryTitle: '摘要',
        summaryText: '学生展现出良好的复原力，适合配合结构化反思活动。',
        nextStepsTitle: '后续建议',
        nextStepsItems: [
          '将报告安全分享给学校心理辅导负责人。',
          '14 天内与家长安排一次跟进沟通。',
          '如有建议，记录在学生档案的照护计划中。',
        ],
      },
    },
  },
  'zh-TW': {
    layout: {
      brandName: 'CoopTest 入口',
      brandTagline: '校園心理健康營運平台',
      nav: {
        home: '首頁',
        dashboard: '儀表板',
        coupons: '優惠券',
        profiles: '檔案與測評',
        present: '成果呈現',
        admin: '管理控制台',
        org: '機構控制台',
        subuser: '子使用者控制台',
        login: '登入',
      },
      roles: {
        admin: '管理員',
        user: '機構使用者',
        subuser: '子使用者',
      },
      roleLabel: '角色：{{role}}',
      logout: '登出',
      navLabel: '主選單',
      themeLabel: '選擇主題',
      languageLabel: '語言',
      themeNames: {
        freshMint: '清新薄荷',
        citrusSunrise: '柑橘晨曦',
        calmSlate: '沉穩石板',
      },
      footer: {
        contact: '聯絡方式',
        email: '信箱：hello@cooptest.com',
        phone: '電話：+1 (800) 555-0132',
        company: '公司資訊',
        about: '關於 CoopTest',
        security: '安全與法遵',
        support: '支援',
        resources: '資源',
        guides: '導入指南',
        privacy: '隱私權政策',
        terms: '使用條款',
        metaTagline: '守護校園心理健康營運的安全流程',
        copyright: '© {{year}} CoopTest 健康科技',
      },
    },
    home: {
      badge: '營運中樞',
      title: '校園心理健康方案的一站式入口',
      description:
        '以單一現代化工作區管理員工入職、優惠券兌換與測評流程。依情境切換主題，協助團隊專注支持學生。',
      stats: {
        schools: { label: '服務學校', value: '86' },
        programs: { label: '進行中專案', value: '312' },
        assessments: { label: '完成測評', value: '18K' },
      },
      actions: {
        title: '快速展開工作',
        description: '通往入口核心作業的捷徑。',
        links: {
          login: '員工登入',
          dashboard: '檢視分析',
          coupons: '管理優惠券',
          profiles: '建立學生檔案',
        },
      },
      reasons: {
        title: '為何選擇 CoopTest？',
        items: [
          '為學生福祉打造精簡的電子紀錄。',
          '支援離線兌換並保留稽核軌跡。',
          '提供值得學校信賴的專業報告。',
        ],
      },
    },
    login: {
      badge: '安全登入',
      title: '員工登入',
      description: '授權團隊可存取儀表板、優惠券庫存與學生檔案。',
      username: '使用者名稱',
      password: '密碼',
      error: '登入失敗，請檢查帳號密碼。',
      loading: '登入中…',
      submit: '登入',
      demo: {
        title: '示範帳號',
        description: '使用下列帳號體驗角色流程。',
        table: {
          role: '角色',
          username: '使用者名稱',
          password: '密碼',
        },
      },
    },
    admin: {
      badge: '平台管理員',
      title: '發放測評額度',
      description: '向機構使用者發放額度並監控餘額。',
      empty: '尚未設定機構。',
      orgSummary: {
        org: '機構',
        user: '機構使用者',
        available: '可用測評',
        totalIssued: '累計發放',
      },
      issue: {
        title: '發放額度',
        description: '向機構使用者新增測評額度。',
        amountLabel: '發放數量',
        amountPlaceholder: '例如 10',
        submit: '發放',
        success: '已更新機構餘額。',
        error: '發放失敗，請檢查數量。',
      },
      ledger: {
        title: '發放紀錄',
        table: {
          date: '時間',
          amount: '數量',
          actor: '操作人',
        },
      },
    },
    orgUser: {
      badge: '機構使用者',
      title: '分配額度給子使用者',
      description: '追蹤機構餘額、分配與檔案結果。',
      empty: '沒有機構資料。',
      orgSummary: {
        org: '機構',
        available: '機構餘額',
        totalIssued: '累計發放',
        profiles: '檔案數量',
        revealed: '已揭示結果',
      },
      allocate: {
        amountPlaceholder: '數量',
        submit: '分配',
        success: '已記錄分配。',
        error: '分配失敗，請檢查餘額。',
      },
      subusers: {
        title: '子使用者分配',
        table: {
          name: '子使用者',
          balance: '可用測評',
          allocate: '分配額度',
        },
      },
      profiles: {
        title: '機構檔案結果',
        hidden: '未揭示',
        table: {
          profile: '檔案',
          subuser: '子使用者',
          status: '狀態',
          tier: '風險等級',
        },
        status: {
          draft: '草稿',
          applied: '已使用',
          revealed: '已揭示',
        },
      },
    },
    subuser: {
      badge: '子使用者',
      title: '為檔案使用測評',
      description: '建立檔案、使用額度並揭示結果。',
      empty: '沒有子使用者資料。',
      balance: {
        available: '可用測評',
        profiles: '建立檔案',
        revealed: '已揭示結果',
      },
      create: {
        title: '建立檔案',
        description: '新增檔案以使用測評額度。',
        placeholder: '檔案名稱',
        submit: '建立檔案',
        success: '檔案已建立。',
        error: '建立失敗，請再試一次。',
      },
      apply: {
        submit: '使用測評',
        error: '無法使用測評額度。',
      },
      reveal: {
        submit: '揭示結果',
        error: '暫時無法揭示結果。',
      },
      profiles: {
        title: '我的檔案',
        hidden: '未揭示',
        table: {
          profile: '檔案',
          status: '狀態',
          tier: '風險等級',
          actions: '操作',
        },
        status: {
          draft: '草稿',
          applied: '已使用',
          revealed: '已揭示',
        },
      },
    },
    access: {
      badge: '無存取權限',
      title: '你沒有權限存取此頁面。',
      description: '請切換帳號或返回首頁。',
      action: '返回首頁',
    },
    dashboard: {
      error: '載入儀表板資料失敗。',
      loading: '儀表板載入中…',
      badge: '營運指標',
      title: '儀表板概覽',
      description: '追蹤優惠券效能與學生成果，對帳離線付款與提供服務。',
      tierLabel: '目前方案：{{tier}}',
      metrics: {
        couponsIssued: '發出優惠券',
        couponValue: '優惠券金額',
        resultsDelivered: '已出具結果',
        testsRemaining: '剩餘測評',
      },
      checklist: {
        title: '對帳清單',
        items: [
          '確認離線付款與已兌換數量相符。',
          '每次測評後更新學生紀錄。',
          '每週與學校合作夥伴分享亮點。',
        ],
      },
    },
    coupons: {
      error: '載入優惠券資料失敗。',
      loading: '優惠券資料載入中…',
      badge: '優惠券',
      title: '定價與兌換管理',
      description: '離線發放兌換碼、追蹤兌換紀錄並監控各校庫存。',
      totalValue: '已兌換總額',
      redeem: {
        title: '兌換優惠券',
        description: '輸入診所櫃檯提供的 CoopTest 兌換碼以啟用服務。',
        placeholder: '請輸入兌換碼',
        submit: '兌換',
        success: '已兌換代碼 {{code}}',
        failure: '兌換失敗，請再試一次。',
      },
      stats: {
        redeemed: '已兌換數量',
        testsUnlocked: '開通測評',
        inventoryHealth: '庫存健康度',
        inventoryLabel: '{{count}} 組代碼',
      },
      pricing: {
        title: '方案價格',
        description: '與學校規劃批次購買時可參考這些方案。',
        tiers: [
          {
            name: '入門版',
            price: 'NT$100',
            features: '15 次測評',
            includes: '含 15 份測評報告',
          },
          {
            name: '進階版',
            price: 'NT$500',
            features: '100 次測評 + 顧問服務',
            includes: '含 100 份測評報告',
          },
          {
            name: '旗艦版',
            price: 'NT$2000',
            features: '800 次測評 + 1:1 輔導',
            includes: '含 800 份測評報告',
          },
        ],
      },
      history: {
        title: '最新兌換紀錄',
        table: {
          code: '代碼',
          value: '金額',
          redeemed: '兌換日期',
        },
      },
    },
    profiles: {
      generatedName: '學生 {{index}}',
      loading: '檔案載入中…',
      badge: '檔案',
      title: '學生建檔與排程',
      description: '兌換後建立學生檔案、上傳名單並追蹤測評安排。',
      readyLabel: '可用檔案',
      history: {
        title: '檔案紀錄',
        searchPlaceholder: '以學生姓名搜尋',
        table: {
          id: '編號',
          name: '姓名',
          dob: '出生日期',
          created: '建立日期',
        },
      },
      upload: {
        title: '批次上傳名冊',
        description: '匯入校務系統輸出的 CSV 名冊，快速產生帳號。',
        success: '已排程 {{file}} 進行處理（示範模式）。',
      },
      form: {
        title: '建立檔案並安排測評',
        nameLabel: '姓名',
        namePlaceholder: '例如：林晨宇',
        dobLabel: '出生日期',
        slotLabel: '首選時段',
        slotOptions: ['上午 09:00', '中午 12:30', '下午 15:00'],
        notesLabel: '備註',
        notesPlaceholder: '新增調整需求或家長訴求。',
        submit: '建立並指派測評',
      },
    },
    present: {
      loading: '呈現資料載入中…',
      badge: '成果',
      title: '呈現測評成果',
      description: '選擇學生檢視最新 CoopTest 成果，在與校方分享前快速掌握重點。',
      availableLabel: '可用檔案',
      select: {
        title: '選擇檔案',
        searchPlaceholder: '以姓名搜尋',
        actionsLabel: '操作',
        button: '檢視結果',
        table: {
          id: '編號',
          name: '姓名',
          dob: '出生日期',
          created: '建立日期',
        },
      },
      report: {
        title: '{{name}} 的結果',
        assessmentId: '測驗編號：{{id}}',
        status: '報告已就緒',
        loading: '結果載入中…',
        resultNumber: '結果編號',
        summaryTitle: '摘要',
        summaryText: '學生展現良好復原力，適合搭配結構化反思活動。',
        nextStepsTitle: '後續行動',
        nextStepsItems: [
          '將報告安全分享給學校輔導窗口。',
          '14 天內與家長安排追蹤會談。',
          '若有建議，記錄於學生檔案的支持計畫。',
        ],
      },
    },
  },
};
